
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { UpdateProductRequest, Product } from '@/shared/types/productTypes';
import { useUpdateProduct } from "@/domains/products/services/products";
import { ERROR, SUCCESS } from "@/config/toast-message-config";
import { showError, showErrorWithApiError, showSuccess } from "@/shared/utils/toast-utils";

interface UpdateProductDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  product: Product | null;
}

const UpdateProductDialog = ({
  open,
  onOpenChange,
  onSuccess,
  product,
}: UpdateProductDialogProps) => {
  const updateProduct = useUpdateProduct();
  const [formData, setFormData] = useState<UpdateProductRequest>({
    orbiusProductName: product?.orbiusProductName || '',
  });

  // Update form data when product changes
  useEffect(() => {
    if (product) {
      setFormData({
        orbiusProductName: product.orbiusProductName,
      });
    }
  }, [product]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!product || !formData.orbiusProductName.trim()) {
      showError(ERROR.SUBSCRIPTION.UPDATE);
      return;
    }

    try {
      await updateProduct.mutateAsync({
        productId: product.orbiusProductReferenceId,
        data: {
          orbiusProductName: formData.orbiusProductName,
        }
      });
      showSuccess(SUCCESS.SUBSCRIPTION.UPDATED);
      onSuccess();
      onOpenChange(false);
      // Reset form
      setFormData({
        orbiusProductName: product.orbiusProductName,
      });
    } catch (error) {
      showErrorWithApiError(error, ERROR.SUBSCRIPTION.UPDATE);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFormData({
      ...formData,
      orbiusProductName: value,
    });
  };

  // Don't render if no product is provided
  if (!product) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Edit Product Name</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-2">
              <Input
                id="orbiusProductName"
                name="orbiusProductName"
                placeholder="Enter Product Name"
                value={formData.orbiusProductName}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="gap-2"
              onClick={() => onOpenChange(false)}
              disabled={updateProduct.isPending}

            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={updateProduct.isPending}
              className="gap-2">
              {updateProduct.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Editing...
                </>
              ) : (
                'Submit'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default UpdateProductDialog; 